/**
 * 指挥页侧栏拖拽宽度计算（从 Dashboard 拆出）
 */

export function computeSidebarDragWidth({
  startX,
  clientX,
  startWidth,
  containerWidth,
  minNormal = 320,
  maxNormal = 800,
  snapMinPx = 40,
  snapPercent = 10
}) {
  const delta = startX - clientX;
  let newWidth = startWidth + delta;
  const widthPercent = (newWidth / containerWidth) * 100;
  const snapping = widthPercent <= snapPercent;

  if (snapping) {
    newWidth = Math.max(snapMinPx, newWidth);
  } else {
    newWidth = Math.max(minNormal, Math.min(maxNormal, newWidth));
  }

  return { newWidth, widthPercent, snapping };
}

/**
 * 松手后是否吸附收缩
 * @returns {{ collapsed: boolean, width: number, snapping: boolean }}
 */
export function resolveSidebarDragEnd({
  currentWidth,
  startWidth,
  viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 1920,
  snapPercent = 10,
  defaultRestoreWidth = 480,
  minNormal = 320
}) {
  const widthPercent = (currentWidth / viewportWidth) * 100;
  if (widthPercent <= snapPercent) {
    return {
      snapping: true,
      collapsed: true,
      width: startWidth >= minNormal ? startWidth : defaultRestoreWidth
    };
  }
  return {
    snapping: false,
    collapsed: false,
    width: currentWidth
  };
}

/** 手机底部面板拖拽高度（0.1~0.8） */
export function computeMobilePanelHeight({
  startY,
  currentY,
  startHeight,
  screenHeight,
  min = 0.1,
  max = 0.8
}) {
  const deltaY = startY - currentY;
  const deltaHeight = deltaY / screenHeight;
  return Math.max(min, Math.min(max, startHeight + deltaHeight));
}

/** 松手后吸附到 40% */
export function snapMobilePanelHeight(height, options = {}) {
  const { low = 0.35, high = 0.45, snapTo = 0.4 } = options;
  if (height > low && height < high) return snapTo;
  return height;
}

/**
 * 创建 PC 侧栏拖拽 mousedown 处理函数
 */
export function createSidebarDragMouseDownHandler(options = {}) {
  const {
    getSidebarWidth,
    getSidebarEl,
    setSidebarWidth,
    setCollapsed,
    persistCollapsed,
    persistWidth,
    onAfterResize
  } = options;

  return function handleDragMouseDown(e) {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = getSidebarWidth?.() ?? 480;
    const containerWidth = window.innerWidth;
    let currentWidth = startWidth;

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    function onMouseMove(moveEvent) {
      const { newWidth, snapping } = computeSidebarDragWidth({
        startX,
        clientX: moveEvent.clientX,
        startWidth,
        containerWidth
      });
      currentWidth = newWidth;
      setSidebarWidth?.(newWidth);
      const el = getSidebarEl?.();
      if (el) {
        el.style.boxShadow = snapping
          ? '0 0 20px rgba(255, 193, 7, 0.8)'
          : 'none';
      }
    }

    function onMouseUp() {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      const el = getSidebarEl?.();
      if (el) el.style.boxShadow = 'none';

      const result = resolveSidebarDragEnd({ currentWidth, startWidth });
      setSidebarWidth?.(result.width);
      setCollapsed?.(result.collapsed);
      persistCollapsed?.(result.collapsed);
      persistWidth?.(result.width);
      console.log(
        result.snapping
          ? `智能吸附：自动收缩侧边栏，保留展开宽度: ${result.width}px`
          : `侧边栏宽度已调整为: ${result.width}px`
      );
      onAfterResize?.();
    }

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  };
}

/** 根据高度比例同步移动端面板外壳样式 */
export function applyMobilePanelChromeStyles(container, heightRatio) {
  if (!container) return;
  if (heightRatio <= 0.15) {
    container.style.background = 'transparent';
    container.style.borderTop = 'none';
    container.style.boxShadow = 'none';
    return;
  }
  container.style.background = '#eaf3fb';
  container.style.borderTop = '2px solid #1976d2';
  container.style.boxShadow = '0 -2px 10px rgba(0,0,0,0.1)';
}
